import { renderRoot } from 'quartz-core/client';
import UIApplication from 'UIComponents/app/UIApplication';
import { createBrowserHistory } from 'history';
import Raven from 'raven-js';

// I18n Configuration
import createStandardI18nProvider from 'I18n/init/providers/createStandardI18nProvider';
import lang from 'i2l?query=sporks!../lang/en.lyaml';
import registerBasicData from 'I18n/init/register/sync/registerBasicData';
import { initAppTranslations } from 'I18n/init/setup/AppInit';

// Rhumb
import { NavMarker, RhumbProvider } from 'react-rhumb';
import rhumbConfig from '../rhumb-config.yaml';
import historyAdapter from 'react-rhumb/historyAdapterV4';

// Redux

import { Provider } from 'react-redux';
import { configureStore } from './utils/configureStore';
import { startApp } from './actions/AppActions';
import { getAuth } from './actions/AuthActions';
import { Router } from 'react-router-dom';
import { HUBLESS_ROOT_URL, getRootUrl } from './utils/urls';
import initializeUsageTrackers from './utils/analytics/initializeUsageTrackers';
import { Routes } from './components/Routes';
import ErrorBoundary from './components/ErrorBoundary';
import { isInIFrame } from './utils/isInIframe';
import { isHealthCheck } from './utils/isHealthCheck';
import isHubless from './utils/isHubless';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export class App {
  constructor() {
    this.store = configureStore();
  }
  // Kicks off the app. This is intentionally not in the constructor.
  async start() {
    if (!isHealthCheck && !isInIFrame()) {
      Raven.captureMessage(`Notifications app was loaded not in an iframe`, {
        level: 'warning'
      });
      console.warn('Not in iframe - will not load notifications');
      return;
    }
    const I18nProvider = createStandardI18nProvider();
    const langAvailable = initAppTranslations([I18nProvider.register(lang), registerBasicData(I18nProvider)]);
    try {
      const {
        user: {
          locale,
          lang_enabled
        },
        portal
      } = await this.startAuth();
      if (isHubless()) {
        const {
          default: BasicPublicI18nSetup
        } = await import('I18n/init/setup/BasicPublicI18nSetup');
        await BasicPublicI18nSetup.setup({
          lang,
          manualLocaleSettings: {
            locale,
            langEnabled: lang_enabled
          }
        });
      } else {
        const timezone = portal && portal.timezone;
        I18nProvider.setLocale({
          locale,
          langEnabled: lang_enabled,
          timezone
        });
        try {
          await langAvailable;
        } catch (e) {
          console.warn('Language info not available - will not load notifications');
          return;
        }
      }
    } catch (e) {
      /* `startAuth` calls into 'hub-http/userInfo' which subtly does caching of the userInfo
          request which most application do not need to be aware of or care about.
           If the request fails because of authentication then pages will typically redirect.
          However as we are a iframe we will not and should not redirect, this is the job of
          the top window. Therefore we can not optimistically assume that if we are in this
          'start' function that the auth request must succeed.
           If we do not catch and handle the errors this results in a massive amount of unhandled
          exceptions that can easily reach the threshold for opening incidents. So we will capture
          the error and gracefully terminate instead, assuming the redirection will be taken
          care of externally and notifications cannot load in this state.
           Keep in mind that the application has multiple instances depending on the nav component
          so any exceptions that happen in the bootstrapping will be multiplied by the total
          notification nav components that are enabled
      */

      console.warn('Auth info not available - will not load notifications');
      return;
    }
    initializeUsageTrackers();
    this.store.dispatch(startApp());
    this.startRouter();
  }
  startAuth() {
    return this.store.dispatch(getAuth());
  }
  startRouter() {
    const baseUrl = isHubless() ? HUBLESS_ROOT_URL : getRootUrl();
    const history = createBrowserHistory({
      basename: baseUrl
    });
    renderRoot( /*#__PURE__*/_jsx(UIApplication, {
      name: "notifications",
      children: /*#__PURE__*/_jsx(Provider, {
        store: this.store,
        children: /*#__PURE__*/_jsx(RhumbProvider, {
          history: historyAdapter(history),
          config: rhumbConfig,
          children: /*#__PURE__*/_jsx(ErrorBoundary, {
            boundaryName: "NOTIFICATIONS_IN_APP_ERROR",
            children: /*#__PURE__*/_jsxs(Router, {
              history: history,
              children: [/*#__PURE__*/_jsx(NavMarker, {
                name: "NOTIFICATIONS_IN_APP_LOAD"
              }), /*#__PURE__*/_jsx(Routes, {})]
            })
          })
        })
      })
    }));
  }
}